// 試遊の記録係の芯 (2026-10-31 に src/dev_playlog.ts から分けた)。
//
// 使う所は2つ:
//   - 開発版の記録 (src/dev_playlog.ts) = 検収パックの間だけ dev サーバーの playlogs/ へ書く。
//   - 配布版の記録 (src/playtest_upload.ts) = 同意した友達の試遊を作者へ送る (functions/api/playlog.ts)。
// どちらも同じ形 (PlaylogChunk) を送る = 要約 (scripts/playlog_summary.mjs) をそのまま使える。
// この module は送り先を知らない (env.send が決める) ので、配布版に入っても開発用の口は漏れない。
// 記録の単位・列・イベントの説明は src/dev_playlog.ts の冒頭と docs/ai/playlog.md。

import type { ActiveHit, Boss, GameState, Player } from './types'
import type { Input, InputAction } from './input'

export const PLAYLOG_VERSION = 1
// 60Hz の 4 tick ごと = 15Hz (約67ms)。 状態が変わった tick は別に必ず1行書くので、
//   区切りの瞬間は tick 精度で残る。 60Hz 全部だと 1分あたり約 300KB、 15Hz+変化行で約 90KB。
//   「どの距離に居続けたか」を見るには 67ms で足りる (歩きは 1tick 数px)。
export const SAMPLE_EVERY_TICKS = 4
// 定期送信の間隔。 ページを閉じた時の sendBeacon は 64KB 程度が上限なので、
//   未送信分が溜まりすぎないよう短めにする (10秒 ≒ 20KB)。
export const FLUSH_EVERY_MS = 10_000
// メモ (F) の前後として要約に出す範囲。 F は「起きた瞬間」ではなく「後から付ける栞」
//   (ユーザー「起こった瞬間に押すのは無理だと思うから」) = 押す前を長く、 後を短く見る。
//   ヘッダの markContext に書き込み、 要約 (scripts/playlog_summary.mjs) はそこを読む。
export const MARK_CONTEXT_BEFORE_SEC = 12
export const MARK_CONTEXT_AFTER_SEC = 2
// メモのキー: F (キーボード) / △・Y (パッドの標準マッピング 3 番)。 どちらもゲーム操作に未割り当て。
export const MARK_KEY = 'f'
export const MARK_PAD_BUTTON = 3

export const ROW_COLS = [
  'ms',    // セッション開始からの実時間 ms (スロー・ヒットストップ込みの壁時計)
  'gt',    // ゲーム内時刻 state.time (秒・再戦で 0 に戻る)
  'px',    // 主人公 x (中心)
  'pf',    // 主人公の向き 1=右 / -1=左
  'ps',    // 主人公の state (PlayerState そのまま)
  'php',   // 主人公 HP
  'ppo',   // 主人公 体幹 (溜まるほど危ない)
  'mv',    // 移動入力 -1/0/1 (押している向き。 歩いているかの意図)
  'bx',    // ボス x (中心)
  'bf',    // ボスの向き 1=右 / -1=左
  'bs',    // ボスの state (BossState そのまま: idle/telegraph/active/comboGap/beatGap/recovery/staggered/blink/reappear)
  'atk',   // ボスの現在の技 id (無ければ null)
  'str',   // ボスの現在の連 id (無ければ null)
  'bms',   // ボスの state に入ってからの ms (負値あり)
  'bhp',   // ボス HP
  'bpo',   // ボス 体幹
  'd',     // 主人公↔ボスの中心間距離 px (絶対値)
  'front', // 1=主人公はボスの正面側 / -1=背後側
  'bn',    // ボスの間合い行動 (neutralAction / spacing の action。 無ければ null)
  'room',  // 部屋 (= boss.executesTaken)
] as const

export type PlaylogRow = (string | number | null)[]
export type PlaylogEvent = { ms: number; gt: number; type: string; [k: string]: unknown }

export type PlaylogAttempt = {
  n: number
  startMs: number
  endMs?: number
  result?: 'death' | 'win' | 'restart' | 'left' | 'open'
  cause?: string | null
  room?: number
  bossHp?: number
  playerHp?: number
}

export type PlaylogFileKey = { day: string; hms: string; boss: string }

export type PlaylogChunk = {
  v: number
  sessionId: string
  file: PlaylogFileKey
  header: Record<string, unknown>
  attempts: PlaylogAttempt[]
  marks: number
  final: boolean
  rowsFrom: number
  rows: PlaylogRow[]
  eventsFrom: number
  events: PlaylogEvent[]
}

// 検収パックのページ (src/dev_kensyu_glue.ts) が自分の window.__kensyu に置く「いま何を遊んでいるか」。
//   rev は block/item/src/touches のどれかが変わるたびに増える (記録係は rev だけ見て、 変わった時だけ比べる)。
export type KensyuTouch = {
  seq: number               // パック内で単調増加 (localStorage で上限を切っても重複しない)
  at: number                // Date.now()
  iso: string               // ローカル時刻の ISO (人が読む用)
  item: string              // 'F-1' など
  block: string | null      // data-setup の id
  kind: 'result' | 'focus' | 'src'
  result?: string           // kind='result' の時の判定 ('' = 取り消し)
}
export type KensyuContext = {
  packId: string
  title?: string
  block: string | null
  item: string | null
  src: string | null
  rev: number
  touches?: KensyuTouch[]
}

export type PlaylogEnv = {
  now(): number
  // 送信。 beacon=true はページが閉じる時 (応答を待てない)。 成功したら true。
  send(chunk: PlaylogChunk, beacon: boolean): Promise<boolean> | boolean
  meta?(): Record<string, unknown>
  toast?(msg: string): void
  localStamp?(): { day: string; hms: string; iso: string; epochMs?: number }
  // 検収パックの中で動いている時だけ: 親ページの window.__kensyu を返す
  kensyu?(): KensyuContext | null
  // 定期送信の間隔 (省略時 FLUSH_EVERY_MS)。 配布版の送信は書き込み回数を減らすため長めにする。
  flushEveryMs?: number
}

export type PlaylogDevFlags = { god?: boolean; slow?: number }

type Session = {
  id: string
  bossId: string
  file: PlaylogFileKey
  header: Record<string, unknown>
  startWall: number
  attempts: PlaylogAttempt[]
  attemptOpen: boolean
  rows: PlaylogRow[]       // 未送信分だけ
  rowsSent: number
  events: PlaylogEvent[]   // 未送信分だけ
  eventsSent: number
  marks: number
  lastFlushWall: number
  tick: number
  dodgedHitIds: Set<number>
  lastDev: string
  lastRowKey: string
  lastGt: number
  kRev: number | null
  kBlock: string | null
  kItem: string | null
  kSrc: string | null
  kTouchSeq: number
}

function pad2(n: number): string { return String(n).padStart(2, '0') }

export function defaultLocalStamp(d = new Date()): { day: string; hms: string; iso: string; epochMs: number } {
  const day = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
  const hms = `${pad2(d.getHours())}${pad2(d.getMinutes())}${pad2(d.getSeconds())}`
  const offMin = -d.getTimezoneOffset()
  const sign = offMin >= 0 ? '+' : '-'
  const iso = `${day}T${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`
    + `${sign}${pad2(Math.floor(Math.abs(offMin) / 60))}:${pad2(Math.abs(offMin) % 60)}`
  return { day, hms, iso, epochMs: d.getTime() }
}

const r1 = (v: number): number => Math.round(v * 10) / 10
const r3 = (v: number): number => Math.round(v * 1000) / 1000
const sgn = (f: 'left' | 'right'): number => (f === 'right' ? 1 : -1)

function distOf(p: Player, b: Boss): number { return Math.round(Math.abs(p.x - b.x)) }
// 1 = 主人公がボスの向いている側に居る (正面) / -1 = 背後
function frontOf(p: Player, b: Boss): number {
  const side = p.x >= b.x ? 1 : -1
  return side === sgn(b.facing) ? 1 : -1
}
function bossNeutral(b: Boss): string | null {
  return b.spacing?.action ?? b.neutralAction?.action ?? null
}

const TRACKED_ACTIONS: InputAction[] = ['attack', 'parry', 'step', 'pause', 'retry']

function lastTouchSeq(k: KensyuContext | null): number {
  let m = 0
  for (const t of k?.touches ?? []) if (t.seq > m) m = t.seq
  return m
}

export class PlaylogRecorder {
  private session: Session | null = null
  private pendingRecv: PlaylogEvent[] = []
  private prevHold: Partial<Record<InputAction, boolean>> = {}
  private prevPressed = new Set<InputAction>()
  private prevMove = 0
  private padMarkHeld = false
  private seq = 0

  constructor(private env: PlaylogEnv) {}

  get active(): boolean { return this.session !== null }
  get currentFile(): PlaylogFileKey | null { return this.session?.file ?? null }
  get currentSessionId(): string | null { return this.session?.id ?? null }

  // ---- hits.ts から: ボスの攻撃1つが主人公に対して「どう解決したか」 ----
  //   res: hit=被弾 / parry=弾き / guard=長押し受け / dodge=回避無敵で抜けた / mikiri=見切り (反撃窓)
  //        grab=掴まれた / knockback=無害な押し出し
  recv(res: string, hit: ActiveHit, p: Player, b: Boss, time: number, extra?: Record<string, unknown>): void {
    const s = this.session
    if (!s) return
    if (res === 'dodge' || res === 'mikiri') {
      // 回避の無敵は重なっている間ずっと通る (持続ゾーン)。 1つの判定につき1回だけ残す。
      if (s.dodgedHitIds.has(hit.id)) return
      s.dodgedHitIds.add(hit.id)
    }
    this.pendingRecv.push({
      ms: Math.round(this.env.now() - s.startWall),
      gt: r3(time),
      type: 'recv',
      res,
      atk: hit.attackId,
      kind: hit.kind,
      ...(hit.follow === 'travel' ? { proj: true, hx: Math.round(hit.x ?? 0) } : {}),
      d: distOf(p, b),
      px: Math.round(p.x),
      bx: Math.round(b.x),
      front: frontOf(p, b),
      pst: p.state,
      bst: b.state,
      ...extra,
    })
  }

  // ---- main.ts の固定 tick ループから: updateGame の直後に1回 ----
  tick(prev: GameState, next: GameState, input: Input, dev: PlaylogDevFlags = {}): void {
    const now = this.env.now()
    // ボスが変わった / タイトルへ戻った = セッションを閉じる
    if (this.session && (next.boss.id !== this.session.bossId || next.phase === 'title')) {
      this.closeAttempt(prev, 'left', now)
      this.endSession(now)
    }
    if (!this.session) {
      if (next.phase !== 'playing') { this.rememberInput(input); this.pendingRecv = []; return }
      this.startSession(next, now)
    }
    const s = this.session!
    s.tick += 1
    const ms = Math.round(now - s.startWall)
    const gt = r3(next.time)
    s.lastGt = gt
    const ev = (type: string, fields: Record<string, unknown> = {}): void => {
      s.events.push({ ms, gt, type, ...fields })
    }
    const where = (st: GameState): Record<string, unknown> => ({
      d: distOf(st.player, st.boss), px: Math.round(st.player.x), bx: Math.round(st.boss.x),
      front: frontOf(st.player, st.boss),
    })

    // ---- 試行の開き・閉じ ----
    if (!s.attemptOpen && next.phase === 'playing') {
      this.openAttempt(next, ms)
      if (s.attempts.length > 1) ev('retry', { n: s.attempts.length })
    } else if (s.attemptOpen && prev.phase === 'playing' && next.phase === 'playing'
      && next.time + 0.25 < prev.time) {
      // 幕間メニューの「もう一度」など、 画面を挟まずに作り直された
      this.closeAttempt(prev, 'restart', now)
      this.openAttempt(next, ms)
      ev('retry', { n: s.attempts.length, via: 'restart' })
    }

    // ---- 検収パック: 項目が変わった / 判定ボタンが押された ----
    this.trackKensyu(s, ev)

    // ---- 入力 ----
    this.recordInput(input, next, ev)

    // ---- 状態の差分から拾うイベント ----
    if (prev.phase !== next.phase) ev('phase', { from: prev.phase, to: next.phase })
    if (prev.paused !== next.paused) ev('pause', { on: next.paused })
    const devKey = `${dev.god ? 1 : 0}/${dev.slow ?? 1}`
    if (devKey !== s.lastDev) { ev('dev', { god: !!dev.god, slow: dev.slow ?? 1 }); s.lastDev = devKey }

    if (next.phase === 'playing' || prev.phase === 'playing') {
      const pb = prev.boss, nb = next.boss, pp = prev.player, np = next.player
      const a0 = pb.currentAttack?.id ?? null
      const a1 = nb.currentAttack?.id ?? null
      if (a1 && (a1 !== a0 || (nb.state === 'telegraph' && pb.state !== 'telegraph'))) {
        ev('bossAtk', { atk: a1, str: nb.currentString?.id ?? null, beat: nb.beatIndex, bs: nb.state,
          ...where(next), pst: np.state })
      }
      // 主人公の攻撃がボスに触れた (命中 / ボスの受け / 見切り反撃)
      if (!next.finisher && !prev.finisher) {
        const contact = next.sfx.includes('counter_hit') ? 'counter'
          : next.sfx.includes('guard') ? 'guarded'
          : next.sfx.includes('hit_boss') ? (np.state === 'attacking' ? (np.attackContactKind ?? 'hit') : 'reflect')
          : null
        if (contact) {
          ev('hitBoss', { contact, dmg: r1(pb.hp - nb.hp), post: r1(nb.posture - pb.posture),
            bs: pb.state, atk: a0, ...where(next) })
        }
      }
      if (nb.state === 'staggered' && pb.state !== 'staggered') {
        ev('bossStagger', { bhp: r1(nb.hp), bpo: r1(nb.posture), ...where(next) })
      }
      if (np.state === 'stunned' && pp.state !== 'stunned') ev('playerStun', { ppo: r1(np.posture), ...where(next) })
      if (np.state === 'executing' && pp.state !== 'executing') {
        ev('deathblow', { variant: np.executeVariant, ...where(next) })
      }
      if (np.state === 'grabbed' && pp.state !== 'grabbed') ev('grabbed', { atk: a1, ...where(next) })
      if (nb.executesTaken !== pb.executesTaken) ev('room', { room: nb.executesTaken })
      if (next.finisher && !prev.finisher) ev('bossDown', where(next))
      if (next.deathBeat && !prev.deathBeat) {
        ev('playerDown', { atk: next.deathBeat.causeAttackId ?? null, ...where(next) })
      }
    }

    // hits.ts から届いた受けの結果 (同じ tick の出来事)
    if (this.pendingRecv.length > 0) {
      for (const e of this.pendingRecv) s.events.push({ ...e, ms, gt: e.gt })
      this.pendingRecv = []
    }

    // ---- 標本 ----
    if (next.phase === 'playing') {
      const p = next.player, b = next.boss
      const key = `${p.state}|${b.state}|${b.currentAttack?.id ?? ''}|${bossNeutral(b) ?? ''}|${input.moveX}`
      const changed = key !== s.lastRowKey
      const advanced = next.time !== prev.time
      if (changed || (advanced && s.tick % SAMPLE_EVERY_TICKS === 0)) {
        s.rows.push([
          ms, gt, Math.round(p.x), sgn(p.facing), p.state, r1(p.hp), r1(p.posture), input.moveX,
          Math.round(b.x), sgn(b.facing), b.state, b.currentAttack?.id ?? null, b.currentString?.id ?? null,
          Math.round(b.stateElapsed * 1000), r1(b.hp), r1(b.posture), distOf(p, b), frontOf(p, b),
          bossNeutral(b), b.executesTaken,
        ])
        s.lastRowKey = key
      }
    }

    // ---- 試行の終わり ----
    if (prev.phase === 'playing' && next.phase === 'defeat') {
      this.closeAttempt(next, 'death', now, next.lastDeathAttackId ?? null)
      void this.flush(false)
    } else if (prev.phase === 'playing' && (next.phase === 'victory' || next.phase === 'cleared')) {
      this.closeAttempt(next, 'win', now)
      this.endSession(now)
      return
    }

    if (now - s.lastFlushWall >= (this.env.flushEveryMs ?? FLUSH_EVERY_MS)) void this.flush(false)
  }

  // パッドの △ を毎 tick 渡してもらい、 押した瞬間だけメモにする。
  padMarkButton(down: boolean): void {
    if (down && !this.padMarkHeld) this.mark('pad')
    this.padMarkHeld = down
  }

  mark(src: 'key' | 'pad' | 'api' = 'key'): boolean {
    const s = this.session
    if (!s) {
      this.env.toast?.('📝 戦闘中ではないのでメモは残していません')
      return false
    }
    s.marks += 1
    const ms = Math.round(this.env.now() - s.startWall)
    s.events.push({ ms, gt: s.lastGt, type: 'mark', n: s.marks, src })
    this.env.toast?.(`📝 メモ位置を記録 #${s.marks}`)
    return true
  }

  // 未送信分を送る。 beacon=true はページが閉じる時。
  async flush(beacon: boolean): Promise<boolean> {
    const s = this.session
    if (!s) return false
    return this.sendSession(s, beacon, false)
  }

  // ページを閉じる / 隠れる時。 開いている試行は result='open' のまま送る。
  flushOnHide(): void {
    const s = this.session
    if (!s) return
    const open = s.attempts[s.attempts.length - 1]
    if (open && s.attemptOpen) open.endMs = Math.round(this.env.now() - s.startWall)
    void this.sendSession(s, true, false)
  }

  // ---- 内部 ----
  private startSession(st: GameState, now: number): void {
    const stamp = (this.env.localStamp ?? defaultLocalStamp)()
    const k = this.readKensyu()
    this.seq += 1
    const id = `${stamp.day}T${stamp.hms}-${st.boss.id}-${Math.floor(now)}-${this.seq}`
    this.session = {
      id,
      bossId: st.boss.id,
      file: { day: stamp.day, hms: stamp.hms, boss: st.boss.id },
      header: {
        bossId: st.boss.id,
        bossName: st.boss.displayName,
        startedAt: stamp.iso,
        ...(stamp.epochMs !== undefined ? { startedEpochMs: stamp.epochMs } : {}),
        solo: st.solo,
        practice: st.practice,
        difficulty: st.settings.difficulty,
        player: { maxHp: st.player.hp, x0: Math.round(st.player.x) },
        boss: { maxHp: st.boss.maxHp, maxPosture: st.boss.maxPosture, roomCount: st.boss.roomCount },
        tickHz: 60,
        sampleEveryTicks: SAMPLE_EVERY_TICKS,
        markContext: { beforeSec: MARK_CONTEXT_BEFORE_SEC, afterSec: MARK_CONTEXT_AFTER_SEC },
        markKeys: { keyboard: MARK_KEY.toUpperCase(), pad: `button ${MARK_PAD_BUTTON} (△/Y)` },
        cols: [...ROW_COLS],
        ...(k ? { kensyu: { packId: k.packId, title: k.title ?? null, block: k.block, item: k.item, src: k.src } } : {}),
        ...(this.env.meta?.() ?? {}),
      },
      startWall: now,
      attempts: [],
      attemptOpen: false,
      rows: [],
      rowsSent: 0,
      events: [],
      eventsSent: 0,
      marks: 0,
      lastFlushWall: now,
      tick: 0,
      dodgedHitIds: new Set(),
      lastDev: '',
      lastRowKey: '',
      lastGt: 0,
      kRev: k?.rev ?? null,
      kBlock: k?.block ?? null,
      kItem: k?.item ?? null,
      kSrc: k?.src ?? null,
      kTouchSeq: lastTouchSeq(k),
    }
  }

  private readKensyu(): KensyuContext | null {
    try { return this.env.kensyu?.() ?? null } catch { return null }
  }

  // rev が変わった時だけ比べる (毎 tick はプロパティ1つ読むだけ)。
  private trackKensyu(s: Session, ev: (type: string, f?: Record<string, unknown>) => void): void {
    if (!this.env.kensyu) return
    const k = this.readKensyu()
    if (!k || k.rev === s.kRev) return
    s.kRev = k.rev
    if (k.block !== s.kBlock || k.item !== s.kItem || k.src !== s.kSrc) {
      ev('kensyu', { block: k.block, item: k.item, src: k.src })
      s.kBlock = k.block; s.kItem = k.item; s.kSrc = k.src
    }
    for (const t of k.touches ?? []) {
      if (t.seq <= s.kTouchSeq) continue
      if (t.kind === 'result') ev('answer', { item: t.item, block: t.block, result: t.result ?? '', at: t.at })
    }
    s.kTouchSeq = Math.max(s.kTouchSeq, lastTouchSeq(k))
  }

  private openAttempt(st: GameState, ms: number): void {
    const s = this.session!
    s.attempts.push({ n: s.attempts.length + 1, startMs: ms, result: 'open', room: st.boss.executesTaken })
    s.attemptOpen = true
    s.dodgedHitIds.clear()
  }

  private closeAttempt(st: GameState, result: PlaylogAttempt['result'], now: number, cause?: string | null): void {
    const s = this.session
    if (!s || !s.attemptOpen) return
    const a = s.attempts[s.attempts.length - 1]!
    a.endMs = Math.round(now - s.startWall)
    a.result = result
    if (cause !== undefined) a.cause = cause
    a.room = st.boss.executesTaken
    a.bossHp = r1(st.boss.hp)
    a.playerHp = r1(st.player.hp)
    s.attemptOpen = false
  }

  private endSession(_now: number): void {
    const s = this.session
    if (!s) return
    this.session = null
    this.pendingRecv = []
    void this.sendSession(s, false, true)
  }

  private buildChunk(s: Session, final: boolean): PlaylogChunk {
    return {
      v: PLAYLOG_VERSION,
      sessionId: s.id,
      file: s.file,
      header: s.header,
      attempts: s.attempts.map((a) => ({ ...a })),
      marks: s.marks,
      final,
      rowsFrom: s.rowsSent,
      rows: s.rows.slice(),
      eventsFrom: s.eventsSent,
      events: s.events.slice(),
    }
  }

  // 送った分だけ手元から捨てる。 サーバーは rowsFrom/eventsFrom の位置へ書くので、
  //   同じ分を2回送っても壊れない (失敗時は次回まとめて送り直す)。
  private async sendSession(s: Session, beacon: boolean, final: boolean): Promise<boolean> {
    const chunk = this.buildChunk(s, final)
    s.lastFlushWall = this.env.now()
    let ok = false
    try { ok = await this.env.send(chunk, beacon) } catch { ok = false }
    if (ok || beacon) {
      if (s.rowsSent === chunk.rowsFrom) {
        s.rows.splice(0, chunk.rows.length)
        s.rowsSent += chunk.rows.length
      }
      if (s.eventsSent === chunk.eventsFrom) {
        s.events.splice(0, chunk.events.length)
        s.eventsSent += chunk.events.length
      }
    }
    return ok
  }

  private rememberInput(input: Input): void {
    for (const a of TRACKED_ACTIONS) this.prevHold[a] = input.hold[a]
    this.prevPressed = new Set(input.pressed)
    this.prevMove = input.moveX
  }

  private recordInput(input: Input, st: GameState, ev: (type: string, f?: Record<string, unknown>) => void): void {
    const dev = input.device ?? 'kbm'
    const d = distOf(st.player, st.boss)
    for (const a of TRACKED_ACTIONS) {
      const held = input.hold[a]
      const was = this.prevHold[a] ?? false
      // hold の立ち上がり、 または hold を見逃すほど短い押し (バッファに新しく現れた)
      const pressed = (held && !was) || (input.pressed.has(a) && !this.prevPressed.has(a) && !was)
      if (pressed) ev('input', { act: a, on: true, dev, d, pst: st.player.state })
      else if (!held && was) ev('input', { act: a, on: false, dev })
    }
    if (input.moveX !== this.prevMove) {
      // ボスへ寄る向きか、離れる向きか (x の正負より AI が読みやすい)
      const toBoss = st.boss.x >= st.player.x ? 1 : -1
      const rel = input.moveX === 0 ? 'stop' : input.moveX === toBoss ? 'toward' : 'away'
      ev('input', { act: 'move', v: input.moveX, rel, dev, d })
    }
    this.rememberInput(input)
  }
}

